import React from 'react';
import { motion } from 'framer-motion';
import { Layers, ArrowRight, Users, Building2, Crown, Globe } from 'lucide-react';
export function NCJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1091">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1092">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1093">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1094">
            <span className="w-8 h-px bg-teal" data-id="element-1095"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1096">
              Neurocognitive Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1097"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1098">
            A Lifecycle Framework for Human Capability, Civic Development, and
            Leadership Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1099">
            The Neurocognitive Civic Judgment Index (NCJI) expands the SyNeural
            platform into a broader developmental architecture that helps
            translate capability gaps into growth pathways, support systems,
            institutional readiness, and leadership qualification across the
            full spectrum of social responsibility.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1100">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1101">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1102">
            Where CJI provides the analytical foundation, NCJI expands that
            foundation into a full-spectrum system for human opportunity,
            lifecycle development, institutional readiness, civic resilience,
            and leadership qualification.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1103">
            It is designed not merely to identify limitations, but to convert
            them into pathways. Every meaningful weakness should be paired with
            a development route. Every barrier should be translated into a plan.
            Every plan should connect to some combination of self-directed
            action, institutional support, community reinforcement, or
            public-sector infrastructure.
          </p>
        </motion.div>
      </section>

      {/* From Assessment to Advancement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1104">
        <div className="max-w-5xl mx-auto" data-id="element-1105">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1106">
            <div className="flex items-center gap-4 mb-8" data-id="element-1107">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1108">
                <ArrowRight size={24} data-id="element-1109" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1110">
                From Assessment to Advancement
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed mb-10" data-id="element-1111">
              NCJI operates on a simple progression model that allows the
              framework to identify capability gaps, clarify what they mean,
              recommend concrete interventions, connect individuals or
              institutions to support structures, track progress over time, and
              unlock wider opportunity as readiness improves.
            </p>

            {/* Process Flow */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1112">
              {[{
              step: 'Assess',
              desc: 'Identify gaps'
            }, {
              step: 'Explain',
              desc: 'Clarify meaning'
            }, {
              step: 'Recommend',
              desc: 'Concrete steps'
            }, {
              step: 'Support',
              desc: 'Connect resources'
            }, {
              step: 'Reassess',
              desc: 'Track progress'
            }, {
              step: 'Advance',
              desc: 'Unlock opportunity'
            }].map((item, index) => <motion.div key={item.step} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="text-center" data-id="element-1113">
                  <div className="w-12 h-12 rounded-full bg-teal/10 border-2 border-teal mx-auto mb-3 flex items-center justify-center" data-id="element-1114">
                    <span className="text-sm font-bold text-teal" data-id="element-1115">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-ink-dark text-sm mb-1" data-id="element-1116">
                    {item.step}
                  </h3>
                  <p className="text-xs text-ink-muted" data-id="element-1117">{item.desc}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Human Opportunity and Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-pale" data-id="element-1118">
        <div className="max-w-4xl mx-auto" data-id="element-1119">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1120">
            <div className="flex items-center gap-4 mb-6" data-id="element-1121">
              <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1122">
                <Users size={24} data-id="element-1123" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1124">
                Human Opportunity and Development
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1125">
              A defining feature of NCJI is its Human Opportunity and
              Development framework. The system is designed to increase
              self-awareness, guide individuals toward practical pathways for
              success, and strengthen communities through better alignment of
              talent, support, and resources. Rather than freezing people inside
              static categories, the framework is meant to identify practical
              growth opportunities and make them actionable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lifecycle Evolution */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1126">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1127">
          <div className="flex items-center gap-4 mb-6" data-id="element-1128">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1129">
              <Layers size={24} data-id="element-1130" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1131">
              A Platform That Evolves With Responsibility
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-1132">
            NCJI is structured as a full-spectrum lifecycle framework:
          </p>

          <div className="space-y-6" data-id="element-1133">
            {[{
            stage: 'Early Development',
            focus: 'Emotional regulation, cooperation, self-direction, and foundational reasoning'
          }, {
            stage: 'Education',
            focus: 'Logic, media literacy, planning, and consequential thinking'
          }, {
            stage: 'Adulthood',
            focus: 'Readiness and opportunity-alignment for workforce, entrepreneurship, civic participation, and public service'
          }, {
            stage: 'Institutional & Executive',
            focus: 'Leadership development, civil-service modernization, and high-responsibility decision environments'
          }].map((item, index) => <motion.div key={item.stage} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line" data-id="element-1134">
                <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-1135">
                  {item.stage}
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1136">{item.focus}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Core Capability Domains */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1137">
        <div className="max-w-5xl mx-auto" data-id="element-1138">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1139">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-6 text-center" data-id="element-1140">
              Core Capability Domains
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1141">
              NCJI is built around foundational human capabilities that shape
              readiness across the lifecycle:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1142">
              {[{
              name: 'Reasoning quality',
              link: '#reasoning-quality'
            }, {
              name: 'Consequential thinking',
              link: '#consequential-thinking'
            }, {
              name: 'Manipulation resistance',
              link: '#manipulation-resistance'
            }, {
              name: 'Emotional regulation',
              link: '#emotional-regulation'
            }, {
              name: 'Social cooperation',
              link: '#social-cooperation'
            }, {
              name: 'Self-direction and execution',
              link: '#self-direction'
            }, {
              name: 'Leadership responsibility',
              link: '#leadership-responsibility'
            }].map((domain, index) => <motion.a key={domain.name} href={domain.link} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white p-5 rounded-lg border border-line flex items-center gap-3 hover:border-navy hover:shadow-md transition-all group" data-id="element-1143">
                  <div className="w-2 h-2 rounded-full bg-navy flex-shrink-0" data-id="element-1144"></div>
                  <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-1145">
                    {domain.name}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-1146">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1147" />
                  </svg>
                </motion.a>)}
            </div>

            <p className="text-lg text-ink-muted leading-relaxed mt-10 text-center" data-id="element-1148">
              These domains create a common framework for understanding how
              individuals develop, how institutions strengthen, and how
              leadership can be evaluated more rigorously over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1149">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1150">
          <div className="flex items-center gap-4 mb-8 justify-center" data-id="element-1151">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1152">
              <Building2 size={24} data-id="element-1153" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1154">
              Built for Individuals, Institutions, and Nations
            </h2>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1155">
            NCJI is designed for use across multiple scales and environments:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1156">
            {[{
            icon: <Users size={20} data-id="element-1157" />,
            title: 'Individual Level',
            desc: 'Human development, self-awareness, and opportunity guidance'
          }, {
            icon: <Building2 size={20} data-id="element-1158" />,
            title: 'Organizational Level',
            desc: 'Readiness and capability-building inside institutions'
          }, {
            icon: <Globe size={20} data-id="element-1159" />,
            title: 'Municipal Scale',
            desc: 'Local civic resilience and community strengthening'
          }, {
            icon: <Crown size={20} data-id="element-1160" />,
            title: 'National Scale',
            desc: 'Strategic planning for civic resilience, institutional strengthening, and leadership qualification'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line" data-id="element-1161">
                <div className="flex items-center gap-3 mb-3" data-id="element-1162">
                  <div className="w-10 h-10 rounded-md bg-surface-muted flex items-center justify-center text-navy-light" data-id="element-1163">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-ink-dark" data-id="element-1164">
                    {item.title}
                  </h3>
                </div>
                <p className="text-ink-muted leading-relaxed" data-id="element-1165">{item.desc}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* NCJI Within SyNeural */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1166">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-1167">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1168">
            NCJI Within the SyNeural Ecosystem
          </h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-1169">
            NCJI is the broader architecture built on top of CJI. Where CJI
            provides the analytical and diagnostic foundation, NCJI expands the
            system into lifecycle development, opportunity guidance,
            institutional readiness, and executive-level leadership evaluation.
            Together, CJI and NCJI form a unified platform for measuring,
            developing, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}